import { IChartVariant } from './../../components/data-containers/PositionsTable/shared/shared.d';
import { IGetChartPositions } from '../../components/data-containers/PositionsTable/shared/shared';
import getChartPositionsUrl from 'url-constructors/getChartPositionsUrl';
import { IPositionsTableFetch } from 'components/data-containers/PositionsTable/PositionsTable';
import { mergeCharts } from 'components/data-containers/PositionsTable/shared/hooks/useChartPositions';
import getByUrl from './helper/getByUrl';
import { filterCharts } from 'components/data-containers/PositionsTable/shared/hooks/useFilteredCharts';
import { getChartVariants } from 'components/data-containers/PositionsTable/shared/hooks/useChartVariants';
import { getChartRows } from 'components/data-containers/PositionsTable/shared/hooks/useChartRows';
import { getGlobalChartRows } from 'components/data-containers/PositionsTable/shared/hooks/useGlobalChartRows';
import { getRowCount } from 'components/data-containers/PositionsTable/shared/hooks/useRowCount';

interface IChartVariantsWithRows {
  [key: string]: {
    [key: string]: {
      rows: any[];
      count: number;
    };
  };
}

interface ICreateChartVariantsWithRows
  extends Pick<IPositionsTableFetch, 'variation'> {
  chartVariants: IChartVariant[] | null;
  filteredCharts: any[] | null;
}
/**
 * Create chart variants object including local + global rows
 */
export const createChartVariantsWithRows = ({
  chartVariants,
  filteredCharts,
  variation,
}: ICreateChartVariantsWithRows): IChartVariantsWithRows => {
  const chartsVariantsWithRows: IChartVariantsWithRows = {};

  chartVariants?.forEach((chart: IChartVariant) => {
    const localRows = getChartRows({
      filteredCharts,
      selectedChartVariant: chart,
    });

    const globalRows = getGlobalChartRows(chart, filteredCharts);

    Object.assign(chartsVariantsWithRows, {
      [chart.name]: {
        local: {
          rows: localRows,
        },
        global: {
          rows: globalRows,
        },
        rowCount: getRowCount({
          chartRows: localRows,
          variation,
        }),
      },
    });
  });

  return chartsVariantsWithRows;
};

/**
 * Interceptor returns list of unique charts (every chart object contains the local + global rows and the row count)
 *
 * ## Data flow
 * 1. It will fetches all chart positions for daily + weekly.
 * 2. Merge the 2 together in a new combined shape "charts".
 * 3. Filter "charts" by the topic filters (e.g. country).
 * 4. Get the unique chart variants of the filtered charts.
 * 5. For every unique chart variant find it's local + global rows and row count and store in a new data shape
 * 6. Return the list of all unique chart variants
 */
const positionsTableInterceptor = async (
  input: RequestInfo /* eslint-disable-line */,
  init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData: IPositionsTableFetch
): Promise<Response> => {
  try {
    const {
      entityType,
      entityIds,
      countryIds,
      productType,
      chartType,
      dailyChartType,
      selectedCustomer,
      positionKey,
      countries,
      filters,
      variation,
    } = interceptionData;

    const params = {
      entityType,
      entityIds,
      countryIds: filters?.country || countryIds,
      productType,
    };

    const dailyChartPositionsUrl = getChartPositionsUrl({
      ...params,
      chartType: dailyChartType,
      chartFrequency: 'daily',
      customerFilter:
        selectedCustomer && selectedCustomer.value !== 'all'
          ? [selectedCustomer.value]
          : undefined,
    });
    const dailyChartPositions = await getByUrl<IGetChartPositions>(
      dailyChartPositionsUrl
    );

    const weeklyChartPositionsUrl = getChartPositionsUrl({
      ...params,
      chartType,
      chartFrequency: 'weekly',
      customerFilter:
        selectedCustomer && selectedCustomer.value !== 'all'
          ? [selectedCustomer.value]
          : undefined,
    });
    const weeklyChartPositions = await getByUrl<IGetChartPositions>(
      weeklyChartPositionsUrl
    );

    const charts = mergeCharts({
      dailyChartPositions,
      weeklyChartPositions,
      positionKey,
      countries,
    });

    const filteredCharts = filterCharts({
      charts,
      filters,
    }) as any[];

    const chartVariants = getChartVariants({
      filteredCharts,
      selectedCustomer,
    });

    const chartVariantsWithRows = createChartVariantsWithRows({
      chartVariants,
      filteredCharts,
      variation,
    });

    return new Response(
      JSON.stringify({ chartVariants: chartVariantsWithRows })
    );
  } catch (error) {
    return new Response(JSON.stringify({ reason: new Error(error) }));
  }
};

export default positionsTableInterceptor;
